
import { useState } from "react";
import { supabaseClient } from "@/lib/supabase/client"
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { VerifiedBadge } from "@/components/ui/VerifiedBadge";
import { createBrowserSupabaseClient } from "@/lib/supabase/client";
import { formatCount } from "@/lib/utils/format";
import { useToast } from "@/components/ui/ToastProvider";
import { cn } from "@/lib/utils/cn";

interface TopicHeaderProps {
  topic: {
    id: string; name: string; slug: string; description: string | null;
    color: string; banner_url: string | null; icon_url: string | null;
    post_count: number; subscriber_count: number; is_verified: boolean;
  };
  isSubscribed: boolean;
  userId: string | null;
}

export function TopicHeader({ topic, isSubscribed: initialSubscribed, userId }: TopicHeaderProps) {
  const [subscribed, setSubscribed] = useState(initialSubscribed);
  const [loading, setLoading] = useState(false);
  const { info } = useToast();
  const navigate = useNavigate();
  const supabase = supabaseClient;

  const handleSubscribe = async () => {
    if (!userId) { info("Sign in to join topics", "Create a free account to follow communities."); return; }
    setLoading(true);
    const prev = subscribed;
    setSubscribed(!prev);
    try {
      if (prev) {
        await supabase.from("topic_subscriptions").delete().eq("user_id", userId).eq("topic_id", topic.id);
      } else {
        // @ts-ignore
        await supabase.from("topic_subscriptions").insert({ user_id: userId, topic_id: topic.id } as any);
      }
      // navigate.refresh() - not needed in Vite;
    } catch {
      setSubscribed(prev);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Banner */}
      <div className="h-32 sm:h-48 relative overflow-hidden"
        style={{ background: topic.banner_url ? undefined : `linear-gradient(135deg, ${topic.color}40, ${topic.color}10)` }}>
        {topic.banner_url && (
          <img src={topic.banner_url} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
      </div>

      {/* Topic info */}
      <div className="container-app">
        <div className="flex items-end justify-between -mt-8 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-2xl border-4 border-background shadow-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${topic.color}20` }}>
              {topic.icon_url
                ? <img src={topic.icon_url} alt="" className="w-full h-full object-cover rounded-xl" />
                : <div className="w-6 h-6 rounded-full" style={{ backgroundColor: topic.color }} />
              }
            </div>
            <div className="pb-1">
              <div className="flex items-center gap-1.5">
                <h1 className="text-xl font-bold text-foreground">{topic.name}</h1>
                {topic.is_verified && <VerifiedBadge size="sm" />}
              </div>
              <div className="flex items-center gap-3 text-xs text-text-muted mt-0.5">
                <span>{formatCount(topic.subscriber_count)} members</span>
                <span>·</span>
                <span>{formatCount(topic.post_count)} posts</span>
              </div>
            </div>
          </div>

          <motion.button onClick={handleSubscribe} disabled={loading} whileTap={{ scale: 0.96 }}
            className={cn("btn-md mb-1 min-w-[90px]",
              subscribed ? "btn-secondary" : "btn-primary"
            )}>
            {loading ? "..." : subscribed ? "Joined" : "Join"}
          </motion.button>
        </div>

        {topic.description && (
          <p className="text-sm text-text-muted mb-4 max-w-2xl">{topic.description}</p>
        )}
      </div>
    </div>
  );
}
